import * as React from 'react';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled, useColorScheme } from '@mui/material/styles';
import AppTheme from '../../shared-theme/AppTheme';
import ColorModeSelect from '../../shared-theme/ColorModeSelect';
import { GoogleIcon } from '../../components/CustomIcons';
import logoCabanaWhite from '../../images/logonextcabana-white.svg';
import logoCabanaBlack from '../../images/logonextcabana-dark.svg';
import NameInput from '../../components/NameInput';
import EmailInput from '../../components/EmailInput';
import PasswordInput from '../../components/PasswordInput';
import { isValidEmail, validatePassword } from '../profile/validationUserDates';
import { useNavigate } from 'react-router-dom';
import { auth, entrarComGoogle, registrarComEmailESenha } from '../../../contexts/authentication';
import { fetchSignInMethodsForEmail, sendEmailVerification } from 'firebase/auth';
import WarningAlert from '../../components/WarningAlert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import Loading from '../../components/Loading';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  maxHeight: '90vh',
  // overflowY: 'hidden',
  padding: theme.spacing(4),
  paddingBottom: '10px',
  gap: theme.spacing(1),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(0, 0%, 0%, 0.05) 0px 5px 15px 0px, hsla(0, 0%, 0%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(0, 0%, 0%, 0.5) 0px 5px 15px 0px, hsla(0, 0%, 0%, 0.08) 0px 15px 35px -5px',
  }),
  '& .css-11ma188-MuiInputBase-root-MuiOutlinedInput-root': {
    height: '40px',
    width: 'auto'
  },
  '@media (orientation: landscape)': {
    overflowY: 'auto',
  },
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100dvh',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    // Modo claro: gradiente verde bem claro para o fundo off-white
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsla(115, 78%, 91%, 1.00), #fffcf2)',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      // Modo escuro: gradiente verde escuro para o fundo preto
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(121, 73%, 13%, 0.50), #0e0e0e)',
    }),
  },
}));

export default function SignUpCard(props: { disableCustomTheme?: boolean }) {
  return (
    <AppTheme {...props}>
      <SignUpContent />
    </AppTheme>
  )
}

function SignUpContent() {
  const { mode, systemMode } = useColorScheme();
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [passwordValidations, setPasswordValidations] = useState({
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    isLengthValid: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (alert) {
      setTimeout(() => {
        setAlert(false)
      }, 6000)
    }
  }, [alert])

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        navigate("/dashboard"); // Redireciona se o usuário estiver logado
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe(); // Limpa o listener ao desmontar o componente
  }, []);

  const handleSignup = async (e: any) => {
    e.preventDefault();

    if (!name || name.trim() === "") {
      setAlert(true)
      setAlertMessage("O campo de nome é obrigatório.")
      return;
    }

    if (!isValidEmail(email)) {
      setAlert(true)
      setAlertMessage("O e-mail informado não é válido.")
      return;
    }

    const { validations, errorMessage } = validatePassword(password);
    setPasswordValidations(validations);

    if (errorMessage) {
      setAlert(true)
      setAlertMessage(errorMessage)
      return;
    }

    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      if (signInMethods.length > 0) {
        setAlert(true)
        setAlertMessage("O e-mail informado já está cadastrado.")
        return;
      }

      const userCredential = await registrarComEmailESenha(name, email, password);
      const user = userCredential.user;
      await sendEmailVerification(user);
    } catch (err) {
      setAlert(true)
      setAlertMessage("Email já cadastrado! Clique em ENTRAR para fazer o login com este email.")
      console.error(err);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <CssBaseline enableColorScheme />
      <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem' }} />
      <SignUpContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={mode === 'dark' || systemMode === 'dark' ? logoCabanaWhite : logoCabanaBlack}
              alt="logo-cabanalink"
              style={{ width: '200px', height: 'auto', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
          </div>
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(1rem, 10vw, 1.15rem)' }}
          >
            Cadastrar
          </Typography>
          <Box
            component="form"
            onSubmit={handleSignup}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <FormControl>
              {/* <FormLabel htmlFor="name">Nome completo</FormLabel> */}
              <NameInput name={name} setName={setName} />
            </FormControl>
            <FormControl>
              {/* <FormLabel htmlFor="email">Email</FormLabel> */}
              <EmailInput email={email} setEmail={setEmail} />
            </FormControl>
            <FormControl sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
              <PasswordInput
                password={password}
                setPassword={setPassword}
                labelMessage='Senha'
              />
              <FormLabel
                htmlFor="password"
                sx={{ display: 'flex', alignItems: 'center', alignSelf: 'center', m: 0 }}
              >
                <Tooltip
                  title="A senha deve conter de 6 a 10 caracteres, letra maiúscula e minúscula, e número."
                  arrow
                  placement="top"
                  enterTouchDelay={0}
                  leaveTouchDelay={7000}
                >
                  <InfoOutlinedIcon sx={{
                    fontSize: '1.5rem',
                  }}
                  />
                </Tooltip>
              </FormLabel>
            </FormControl>
            <Button
              type="submit"
              fullWidth
              variant="contained"
            >
              Cadastrar
            </Button>
          </Box>
          <Divider>
            <Typography sx={{ color: 'text.secondary' }}>ou</Typography>
          </Divider>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => entrarComGoogle()}
              startIcon={<GoogleIcon />}
            >
              Cadastrar com Google
            </Button>
            <Typography sx={{ textAlign: 'center' }}>
              Já possui uma conta?{' '}
              <Link
                href="/signin"
                variant="body2"
                sx={{ alignSelf: 'center' }}
              >
                Entrar
              </Link>
            </Typography>
          </Box>
        </Card>
      </SignUpContainer>
      {alert && (
        <WarningAlert message={alertMessage} />
      )}
    </>
  );
}